import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    InventoryTransferCreateDTO,
    InventoryTransferDTO,
} from "../../../types/inventory/transfer/InventoryTransferTypes";

// ---------------------------------------------------------------
// 🟩 Estado inicial del formulario (solo create)
// ---------------------------------------------------------------
const initialForm: InventoryTransferDTO = {
    originBranchId: 0,
    destinationBranchId: 0,
    details: [],
    reason: null,
    id: 0,
    originBranchName: "",
    destinationBranchName: "",
    status: "",
    requestedById: 0,
    requestedByName: "",
    requestedDate: "",
    requestedTime: "",
    reversed: false,
    createdDate: "",
    createdTime: "",
    updatedDate: "",
    updatedTime: "",
};

// ---------------------------------------------------------------
// 🟦 Estado principal del slice
// ---------------------------------------------------------------
interface InventoryTransferState {
    form: InventoryTransferDTO;
    formErrors: Record<string, string>;
    isSubmitting: boolean;

    // 🧭 Modales
    showCreateModal: boolean;
    showUpdateModal: boolean;
    showDetailModal: boolean;
    showApproveModal: boolean;
    showConfirmModal: boolean;
    showCancelModal: boolean;
    showReverseModal: boolean;
    showRejectModal: boolean;
}

const initialState: InventoryTransferState = {
    form: initialForm,
    formErrors: {},
    isSubmitting: false,
    showCreateModal: false,
    showUpdateModal: false,
    showDetailModal: false,
    showApproveModal: false,
    showConfirmModal: false,
    showCancelModal: false,
    showReverseModal: false,
    showRejectModal: false
};

// ---------------------------------------------------------------
// 🟧 Slice
// ---------------------------------------------------------------
const inventoryTransferSlice = createSlice({
    name: "inventoryTransfer",
    initialState,
    reducers: {
        // ------------------------------------------------------------------
        // 📝 FORM — actualizar campos (create/update)
        // ------------------------------------------------------------------
        updateTransferField(
            state,
            action: PayloadAction<{ field: keyof InventoryTransferCreateDTO | keyof InventoryTransferDTO; value: any }>
        ) {
            const { field, value } = action.payload;
            state.form = {
                ...state.form,
                [field]: value,
            };
        },

        // ------------------------------------------------------------------
        // 📄 Setear formulario completo (UPDATE / DETAIL)
        // ------------------------------------------------------------------
        setTransferForm(state, action: PayloadAction<InventoryTransferDTO>) {
            state.form = action.payload;
            state.formErrors = {};
        },

        // ------------------------------------------------------------------
        // 🔄 Reset del form a modo CREATE
        // ------------------------------------------------------------------
        resetForm(state) {
            state.form = initialForm;
            state.formErrors = {};
            state.isSubmitting = false;
        },

        // ------------------------------------------------------------------
        // ❌ FORM — manejo de errores
        // ------------------------------------------------------------------
        clearFormErrors(state) {
            state.formErrors = {};
        },
        setFormErrors(state, action: PayloadAction<Record<string, string>>) {
            state.formErrors = action.payload;
        },

        // ------------------------------------------------------------------
        // ⏳ Estado de envío
        // ------------------------------------------------------------------
        setIsSubmitting(state, action: PayloadAction<boolean>) {
            state.isSubmitting = action.payload;
        },

        // ------------------------------------------------------------------
        // 🧭 Modales
        // ------------------------------------------------------------------
        openCreateModal(state) {
            state.showCreateModal = true;
            state.form = initialForm;
            state.formErrors = {};
        },
        closeCreateModal(state) {
            state.showCreateModal = false;
        },

        openUpdateModal(state) {
            state.showUpdateModal = true;
            state.formErrors = {};
        },
        closeUpdateModal(state) {
            state.showUpdateModal = false;
        },

        openDetailModal(state) {
            state.showDetailModal = true;
        },
        closeDetailModal(state) {
            state.showDetailModal = false;
        },

        openApproveModal(state) {
            state.showApproveModal = true;
        },
        closeApproveModal(state) {
            state.showApproveModal = false;
        },

        openConfirmModal(state) {
            state.showConfirmModal = true;
        },
        closeConfirmModal(state) {
            state.showConfirmModal = false;
        },

        openCancelModal(state) {
            state.showCancelModal = true;
        },
        closeCancelModal(state) {
            state.showCancelModal = false;
        },

        openReverseModal(state) {
            state.showReverseModal = true;
        },
        closeReverseModal(state) {
            state.showReverseModal = false;
        },
        openRejectModal(state) {
            state.showRejectModal = true;
        },
        closeRejectModal(state) {
            state.showRejectModal = false;
        },


        // ------------------------------------------------------------------
        // ♻ Reset completo del slice
        // ------------------------------------------------------------------
        resetTransferSlice(state) {
            state.form = initialForm;
            state.formErrors = {};
            state.isSubmitting = false;

            state.showCreateModal = false;
            state.showUpdateModal = false;
            state.showDetailModal = false;
        },
    },
});

// ---------------------------------------------------------------
// Exportar acciones + reducer
// ---------------------------------------------------------------
export const {
    updateTransferField,
    setTransferForm,
    clearFormErrors,
    setFormErrors,
    setIsSubmitting,
    resetForm,
    openCreateModal,
    closeCreateModal,
    openUpdateModal,
    closeUpdateModal,
    openDetailModal,
    closeDetailModal,
    resetTransferSlice,

    openApproveModal,
    closeApproveModal,
    openConfirmModal,
    closeConfirmModal,
    openCancelModal,
    closeCancelModal,
    openReverseModal,
    closeReverseModal,
    openRejectModal,
    closeRejectModal,
} = inventoryTransferSlice.actions;

export default inventoryTransferSlice.reducer;
